import { useRef, useEffect, useState, useMemo, useCallback } from 'react';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import { formatDate, hasTextContent, stripHtml, getContrastColor } from '../utils';
import Modal from './Modal';
import Icon from './Icon';

// ─── ZIGZAG TIMELINE COMPONENT ────────────────────────────────────────────────
//
//  Props:
//    events       {array}    list of event objects
//    colors       {object}   { primary, secondary, tertiary, spineColor? }
//    onEventClick {function} receives the clicked event object
//    manualOrder  {boolean}  sort by sortOrder instead of startDate
//    timeline     {object}   full timeline object
//    isEmbed      {boolean}  hides toolbar/export chrome
//

// No module-level state — stagger is handled per-card via mount timestamp.

// ─── HELPER ───────────────────────────────────────────────────────────────────

const safeSlug = (title) =>
  (title || 'timeline').replace(/[^a-z0-9]+/gi, '_').replace(/^_+|_+$/g, '') || 'timeline';

// ─── ZigzagDivider ────────────────────────────────────────────────────────────

const ZigzagDivider = ({ label, colors }) => (
  <li className="zz-divider-item" aria-label={`Chapter: ${label}`}>
    <div className="zz-divider">
      <div className="zz-divider-line" style={{ background: `${colors.secondary}40` }} />
      <span
        className="zz-divider-label"
        style={{ color: getContrastColor(colors.tertiary), borderColor: `${colors.secondary}40`, background: `${colors.tertiary}ee` }}
      >
        {label}
      </span>
      <div className="zz-divider-line" style={{ background: `${colors.secondary}40` }} />
    </div>
  </li>
);

// ─── ZigzagFeaturedCard ───────────────────────────────────────────────────────

const ZigzagFeaturedCard = ({ ev, colors, onEventClick }) => {
  const cardRef   = useRef(null);
  const reducedRef = useRef(false);

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    reducedRef.current = mq.matches;
    const onChange = e => { reducedRef.current = e.matches; };
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  useEffect(() => {
    const el = cardRef.current;
    if (!el || reducedRef.current) return;

    el.style.opacity = '0';
    el.style.transform = 'translateY(16px)';
    el.style.willChange = 'transform, opacity';
    el.style.transition = 'opacity 400ms ease, transform 400ms ease';

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        observer.disconnect();
        el.style.opacity = '1';
        el.style.transform = 'translateY(0)';
        el.addEventListener('transitionend', () => { el.style.willChange = 'auto'; }, { once: true });
      },
      { threshold: 0.1 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <li className="zz-featured-item">
      <button
        ref={cardRef}
        className="zz-featured-card"
        onClick={() => onEventClick(ev)}
        onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onEventClick(ev); } }}
        aria-label={`Featured event: ${ev.title}`}
        style={{
          background: `${colors.tertiary}cc`,
          border: `2px solid ${colors.secondary}60`,
        }}
        onMouseEnter={e => { e.currentTarget.style.borderColor = colors.secondary; }}
        onMouseLeave={e => { e.currentTarget.style.borderColor = `${colors.secondary}60`; }}
      >
        <span
          className="zz-featured-badge"
          style={{ background: colors.secondary, color: getContrastColor(colors.secondary) }}
        >
          ★ Featured
        </span>
        {ev.photo && (
          <div
            className="zz-featured-photo"
            style={{ backgroundImage: `url(${ev.photo})` }}
            role="img"
            aria-label={`Photo for ${ev.title}`}
          />
        )}
        <div className="zz-featured-body">
          <h3
            className="zz-featured-title"
            style={{ color: colors.primary }}
            title={ev.title}
          >
            {ev.title}
          </h3>
          <p className="zz-card-date" style={{ color: colors.secondary }}>
            {formatDate(ev.startDate, ev.startEra !== 'NONE')}
            {ev.endDate !== ev.startDate && ` → ${formatDate(ev.endDate, ev.endEra !== 'NONE')}`}
          </p>
          {hasTextContent(ev.description) && (
            <p className="zz-featured-desc">
              {stripHtml(ev.description)}
            </p>
          )}
        </div>
      </button>
    </li>
  );
};

// ─── ZigzagCard ───────────────────────────────────────────────────────────────

const ZigzagCard = ({ ev, isLeft, colors, onEventClick, photoFirst, cardIndex = 0 }) => {
  const cardRef    = useRef(null);
  const reducedRef = useRef(false);

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    reducedRef.current = mq.matches;
    const onChange = e => { reducedRef.current = e.matches; };
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  useEffect(() => {
    const el = cardRef.current;
    if (!el || reducedRef.current) return;

    // Stamp when this effect ran. If the IO fires within 300ms (initial load),
    // apply a stagger delay; if it fires later (scroll reveal), skip the delay.
    const mountTime = Date.now();
    const dx = isLeft ? '-20px' : '20px';
    el.style.opacity    = '0';
    el.style.transform  = `translateX(${dx})`;
    el.style.willChange = 'transform, opacity';

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        observer.disconnect();
        const delay = Date.now() - mountTime < 300 ? cardIndex * 80 : 0;
        el.style.transition = `opacity 300ms ease ${delay}ms, transform 300ms ease ${delay}ms`;
        el.style.opacity    = '1';
        el.style.transform  = 'translateX(0)';
        el.addEventListener('transitionend', () => {
          el.style.willChange = 'auto';
          el.style.transition = 'opacity 300ms ease, transform 300ms ease';
        }, { once: true });
      },
      { threshold: 0.1 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [isLeft, cardIndex]);

  const ariaLabel = `${ev.title}, ${formatDate(ev.startDate, ev.startEra !== 'NONE')} to ${formatDate(ev.endDate, ev.endEra !== 'NONE')}`;
  const cardClass  = `zz-card${photoFirst && ev.photo ? ' zz-card--photofirst' : ''}`;

  return (
    <button
      ref={cardRef}
      className={cardClass}
      onClick={() => onEventClick(ev)}
      onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onEventClick(ev); } }}
      aria-label={ariaLabel}
      style={{
        background: `${colors.tertiary}cc`,
        border: `1px solid ${colors.primary}40`,
      }}
      onMouseEnter={e => { e.currentTarget.style.borderColor = colors.secondary; }}
      onMouseLeave={e => { e.currentTarget.style.borderColor = `${colors.primary}40`; }}
      onFocus={e => {
        e.currentTarget.style.borderColor   = colors.secondary;
        e.currentTarget.style.outline       = `2px solid ${colors.secondary}`;
        e.currentTarget.style.outlineOffset = '2px';
      }}
      onBlur={e => {
        e.currentTarget.style.borderColor = `${colors.primary}40`;
        e.currentTarget.style.outline     = 'none';
      }}
    >
      {ev.photo && (
        <img src={ev.photo} alt="" aria-hidden="true" className="zz-card-photo" loading="lazy" />
      )}
      <div className="zz-card-body">
        <h3 className="zz-card-title" style={{ color: colors.primary }} title={ev.title}>
          {ev.title}
        </h3>
        <p className="zz-card-date" style={{ color: colors.secondary }}>
          {formatDate(ev.startDate, ev.startEra !== 'NONE')}
          {ev.endDate !== ev.startDate && ` → ${formatDate(ev.endDate, ev.endEra !== 'NONE')}`}
        </p>
        {hasTextContent(ev.description) && (
          <p className="zz-card-desc">
            {stripHtml(ev.description)}
          </p>
        )}
      </div>
    </button>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────

const ZigzagTimeline = ({
  events,
  colors,
  onEventClick,
  manualOrder = false,
  timeline,
  isEmbed = false,
}) => {
  const [photoFirst,   setPhotoFirst]   = useState(false);
  const [isExporting,  setIsExporting]  = useState(false);
  const [exportMsg,    setExportMsg]    = useState('');
  const [embedOpen,    setEmbedOpen]    = useState(false);
  const [embedCopied,  setEmbedCopied]  = useState(false);
  const [embedWidth,   setEmbedWidth]   = useState('800');
  const [embedHeight,  setEmbedHeight]  = useState('700');
  const [embedResp,    setEmbedResp]    = useState(true);

  const containerRef = useRef(null);
  const rafRef       = useRef(null);

  const sorted = useMemo(() => [...events].sort((a, b) => {
    if (manualOrder) return (a.sortOrder ?? 0) - (b.sortOrder ?? 0);
    const d = (a.startDate || "").localeCompare(b.startDate || "");
    if (d !== 0) return d;
    return (a.sortOrder ?? 0) - (b.sortOrder ?? 0);
  }), [events, manualOrder]);

  // Build display list: dividers at chapter boundaries, featured cards full-width.
  // Regular events advance the alternating index; featured events do not.
  const displayItems = useMemo(() => {
    const items = [];
    let altIndex  = 0;
    let dotIndex  = 0;
    let lastChapter = null;

    sorted.forEach((ev, i) => {
      const chapter = ev.chapter?.trim() || '';
      if (chapter && chapter !== lastChapter) {
        items.push({ type: 'divider', id: `div-${i}`, label: chapter });
        lastChapter = chapter;
      }
      if (!chapter) lastChapter = null;

      if (ev.featured) {
        items.push({ type: 'featured', ev, id: ev.id, dotIndex: dotIndex++ });
      } else {
        items.push({ type: 'event', ev, id: ev.id, isLeft: altIndex % 2 === 0, dotIndex: dotIndex++ });
        altIndex++;
      }
    });

    return items;
  }, [sorted]);

  // ── Shadow parallax (rAF-throttled, desktop only, respects reduced-motion) ──
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');

    const onScroll = () => {
      if (rafRef.current) return;
      rafRef.current = requestAnimationFrame(() => {
        rafRef.current = null;
        if (window.innerWidth < 768 || mq.matches) return;

        const cards     = container.querySelectorAll('.zz-card');
        const midpoint  = window.innerHeight / 2;

        cards.forEach(card => {
          const rect      = card.getBoundingClientRect();
          const cardMid   = rect.top + rect.height / 2;
          const dist      = Math.abs(cardMid - midpoint);
          const proximity = Math.max(0, 1 - dist / (window.innerHeight / 2));
          const blur      = 4 + proximity * 12;
          const alpha     = 0.08 + proximity * 0.14;
          card.style.boxShadow = `0 ${blur}px ${blur * 2}px rgba(0,0,0,${alpha.toFixed(2)})`;
        });
      });
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (rafRef.current) { cancelAnimationFrame(rafRef.current); rafRef.current = null; }
    };
  }, []);

  // ── Embed ──────────────────────────────────────────────────────────────────
  const embedUrl  = timeline?.id
    ? `${typeof window !== 'undefined' ? window.location.origin : ''}/embed/${timeline.id}`
    : '';
  const safeTitle = (timeline?.title || 'Timeline').replace(/"/g, '&quot;');
  const wAttr     = embedResp ? '100%' : embedWidth;
  const hAttr     = embedResp ? '100%' : embedHeight;
  const embedCode = `<iframe\n  src="${embedUrl}"\n  width="${wAttr}"\n  height="${hAttr}"\n  style="border:none;border-radius:12px;"\n  title="${safeTitle}"\n  allowfullscreen\n></iframe>`;

  const copyEmbed = useCallback(() => {
    navigator.clipboard?.writeText(embedCode).catch(() => {});
    setEmbedCopied(true);
    setTimeout(() => setEmbedCopied(false), 2000);
  }, [embedCode]);

  // ── PNG export ─────────────────────────────────────────────────────────────
  const handleExportPNG = useCallback(async () => {
    setIsExporting(true);
    setExportMsg('Preparing export…');

    try {
      const el = containerRef.current;
      await document.fonts.ready;
      await new Promise(r => setTimeout(r, 150));

      const origOverflow = el.style.overflow;
      const origHeight   = el.style.height;
      el.style.overflow  = 'visible';
      el.style.height    = `${el.scrollHeight}px`;

      let canvas;
      try {
        canvas = await html2canvas(el, {
          scale:           2,
          useCORS:         true,
          allowTaint:      true,
          backgroundColor: null,
          width:           el.offsetWidth,
          height:          el.scrollHeight,
          scrollX:         0,
          scrollY:         -window.scrollY,
        });
      } finally {
        el.style.overflow = origOverflow;
        el.style.height   = origHeight;
      }

      const date = new Date().toISOString().slice(0, 10);
      const link = document.createElement('a');
      link.download = `${safeSlug(timeline?.title)}_${date}.png`;
      link.href     = canvas.toDataURL('image/png');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      alert(`PNG export failed: ${err.message}`);
    } finally {
      setIsExporting(false);
      setExportMsg('');
    }
  }, [timeline]);

  // ── PDF export ─────────────────────────────────────────────────────────────
  const handleExportPDF = useCallback(async () => {
    setIsExporting(true);
    setExportMsg('Generating PDF…');

    try {
      const el = containerRef.current;
      await document.fonts.ready;
      await new Promise(r => setTimeout(r, 150));

      // Snapshot card geometry before capture for page-break avoidance.
      // Use getBoundingClientRect differentials — offsetTop would be measured
      // from .zz-list (which has position:relative), not from the container.
      const containerTop = el.getBoundingClientRect().top;
      const cardEls      = [...el.querySelectorAll('.zz-card, .zz-featured-card')];
      const cardRects    = cardEls.map(c => {
        const r = c.getBoundingClientRect();
        return { top: r.top - containerTop, bottom: r.bottom - containerTop };
      });

      const origOverflow = el.style.overflow;
      const origHeight   = el.style.height;
      el.style.overflow  = 'visible';
      el.style.height    = `${el.scrollHeight}px`;

      let canvas;
      try {
        canvas = await html2canvas(el, {
          scale:           2,
          useCORS:         true,
          allowTaint:      true,
          backgroundColor: null,
          width:           el.offsetWidth,
          height:          el.scrollHeight,
          scrollX:         0,
          scrollY:         -window.scrollY,
        });
      } finally {
        el.style.overflow = origOverflow;
        el.style.height   = origHeight;
      }

      // ── Page constants (A4 portrait) ─────────────────────────────────────────
      const pageW    = 595.28;
      const pageH    = 841.89;
      const MARGIN   = 36;
      const FOOTER_H = 20;
      const LINE_H   = 13;
      const contentW = pageW - MARGIN * 2;

      const titleText  = timeline?.title  || 'Timeline';
      const authorText = timeline?.author || '';
      const descRaw    = timeline?.description ? stripHtml(timeline.description).trim() : '';
      const today      = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

      const pdf = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'a4' });

      pdf.setFontSize(10);
      const descLines = descRaw ? pdf.splitTextToSize(descRaw, contentW) : [];
      let HEADER      = MARGIN + 24;
      if (descLines.length) HEADER += descLines.length * LINE_H + 6;
      if (authorText)       HEADER += LINE_H + 4;
      HEADER += 8;

      const scale2x    = 2;
      const canvasNatW = canvas.width  / scale2x;
      const canvasNatH = canvas.height / scale2x;
      const scaleRatio = contentW / canvasNatW;
      const pageHcss   = (pageH - FOOTER_H - MARGIN) / scaleRatio;

      setExportMsg('Calculating layout…');
      const pageBreaks = [];
      let curY = 0;
      while (curY < canvasNatH) {
        const firstPage    = pageBreaks.length === 0;
        const availH       = firstPage ? (pageH - HEADER - FOOTER_H) / scaleRatio : pageHcss;
        const naturalBreak = curY + availH;

        if (naturalBreak >= canvasNatH) { pageBreaks.push(canvasNatH); break; }

        // If the natural break falls inside a card, push the break up to
        // that card's top so the card starts on the next page intact.
        const splitCard = cardRects.find(({ top, bottom }) =>
          top > curY && top < naturalBreak && bottom > naturalBreak,
        );
        const breakPt = splitCard ? splitCard.top : naturalBreak;
        pageBreaks.push(Math.min(breakPt, canvasNatH));
        curY = breakPt;
      }

      const numTimelinePages = pageBreaks.length;

      setExportMsg('Building events list…');
      const eventsData = sorted.map(ev => {
        const desc    = stripHtml(ev.description || '').trim();
        pdf.setFontSize(10);
        const wrapped = desc ? pdf.splitTextToSize(desc, contentW) : [];
        return {
          title:   ev.title,
          date:    ev.endDate && ev.endDate !== ev.startDate ? `${formatDate(ev.startDate, ev.startEra !== 'NONE')} → ${formatDate(ev.endDate, ev.endEra !== 'NONE')}` : formatDate(ev.startDate, ev.startEra !== 'NONE'),
          wrapped,
        };
      });

      let simY = MARGIN + LINE_H * 2.5;
      let numEventPages = 1;
      for (const ev of eventsData) {
        const blockH = LINE_H * 1.6 + LINE_H + ev.wrapped.length * LINE_H + 10;
        if (simY + blockH > pageH - FOOTER_H - LINE_H) { simY = MARGIN + LINE_H; numEventPages++; }
        simY += blockH;
      }

      const totalPages = numTimelinePages + numEventPages;

      const drawFooter = (pageNum) => {
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(9);
        pdf.setTextColor(160, 160, 160);
        pdf.text('Chronos', MARGIN, pageH - MARGIN / 2);
        pdf.text(`Page ${pageNum} of ${totalPages}`, pageW - MARGIN, pageH - MARGIN / 2, { align: 'right' });
      };

      setExportMsg('Rendering timeline…');
      let curSrcY = 0;
      for (let pageIdx = 0; pageIdx < pageBreaks.length; pageIdx++) {
        if (pageIdx > 0) pdf.addPage();

        if (pageIdx === 0) {
          let hy = MARGIN + 20;
          pdf.setFont('times', 'bold');
          pdf.setFontSize(20);
          pdf.setTextColor(30, 30, 30);
          pdf.text(titleText, MARGIN, hy);
          hy += 26;

          if (descLines.length) {
            pdf.setFont('times', 'normal');
            pdf.setFontSize(10);
            pdf.setTextColor(80, 80, 80);
            pdf.text(descLines, MARGIN, hy);
            hy += descLines.length * LINE_H + 6;
          }

          if (authorText) {
            pdf.setFont('times', 'italic');
            pdf.setFontSize(11);
            pdf.setTextColor(110, 110, 110);
            pdf.text(authorText, MARGIN, hy);
          }

          pdf.setFont('helvetica', 'normal');
          pdf.setFontSize(9);
          pdf.setTextColor(140, 140, 140);
          pdf.text(today, pageW - MARGIN, MARGIN + 20, { align: 'right' });
        }

        const breakEndY  = pageBreaks[pageIdx];
        const sliceH_css = breakEndY - curSrcY;
        const slice      = document.createElement('canvas');
        slice.width      = canvas.width;
        slice.height     = sliceH_css * scale2x;
        slice.getContext('2d').drawImage(canvas, 0, -curSrcY * scale2x);

        const dstH = sliceH_css * scaleRatio;
        const topY = pageIdx === 0 ? HEADER : MARGIN;
        pdf.addImage(slice.toDataURL('image/png'), 'PNG', MARGIN, topY, contentW, dstH);

        drawFooter(pageIdx + 1);
        curSrcY = breakEndY;
      }

      setExportMsg('Writing events…');
      pdf.addPage();
      let evPage = numTimelinePages + 1;
      let y = MARGIN + LINE_H;

      pdf.setFont('times', 'bold');
      pdf.setFontSize(16);
      pdf.setTextColor(30, 30, 30);
      pdf.text(`All Events  (${eventsData.length})`, MARGIN, y);
      y += LINE_H * 2;

      pdf.setDrawColor(180, 180, 180);
      pdf.setLineWidth(0.5);
      pdf.line(MARGIN, y - LINE_H * 0.5, pageW - MARGIN, y - LINE_H * 0.5);

      for (const ev of eventsData) {
        const blockH = LINE_H * 1.6 + LINE_H + ev.wrapped.length * LINE_H + 10;
        if (y + blockH > pageH - FOOTER_H - LINE_H) {
          drawFooter(evPage);
          pdf.addPage();
          evPage++;
          y = MARGIN + LINE_H;
        }
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(12);
        pdf.setTextColor(30, 30, 30);
        pdf.text(ev.title, MARGIN, y);
        y += LINE_H * 1.6;

        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(9);
        pdf.setTextColor(110, 110, 110);
        pdf.text(ev.date, MARGIN, y);
        y += LINE_H;

        if (ev.wrapped.length > 0) {
          pdf.setFont('helvetica', 'normal');
          pdf.setFontSize(10);
          pdf.setTextColor(55, 55, 55);
          pdf.text(ev.wrapped, MARGIN, y);
          y += ev.wrapped.length * LINE_H;
        }

        y += 4;
        pdf.setDrawColor(210, 210, 210);
        pdf.setLineWidth(0.3);
        pdf.line(MARGIN, y, pageW - MARGIN, y);
        y += 6;
      }

      drawFooter(evPage);

      const date = new Date().toISOString().slice(0, 10);
      pdf.save(`${safeSlug(titleText)}_${date}.pdf`);
    } catch (err) {
      alert(`PDF export failed: ${err.message}`);
    } finally {
      setIsExporting(false);
      setExportMsg('');
    }
  }, [sorted, timeline]);

  // ──────────────────────────────────────────────────────────────────────────
  const spineColor = colors.spineColor || colors.secondary;
  const hasPhotos  = sorted.some(ev => ev.photo);
  const isPublic   = !timeline?.privacy || timeline.privacy === 'public';

  if (sorted.length === 0) {
    return (
      <div style={{ padding: 60, textAlign: 'center', color: 'var(--text3)' }}>
        No events yet. Add your first event.
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className={`zz-container${photoFirst ? ' zz-photo-first' : ''}`}
      style={{ '--zz-spine-color': spineColor }}
      data-exporting={isExporting || undefined}
    >
      {/* Export overlay */}
      {isExporting && (
        <div className="zz-export-overlay zz-no-export" aria-live="polite">
          <div className="zz-export-card">
            <div style={{ fontSize: 13, color: 'var(--text2)' }}>{exportMsg}</div>
          </div>
        </div>
      )}

      {/* Toolbar — hidden in embed */}
      {!isEmbed && (
        <div className="zz-toolbar zz-no-export" role="toolbar" aria-label="Timeline controls">
          {hasPhotos && (
            <button
              className={`zz-toolbar-btn${photoFirst ? ' zz-toolbar-btn--active' : ''}`}
              onClick={() => setPhotoFirst(v => !v)}
              style={photoFirst ? { borderColor: colors.secondary, color: colors.secondary } : {}}
            >
              📷 Photo Mode{photoFirst ? ': On' : ''}
            </button>
          )}

          <div style={{ flex: 1 }} aria-hidden="true" />

          <button
            className="zz-toolbar-btn"
            onClick={handleExportPNG}
            title="Export as PNG"
            disabled={isExporting}
          >
            <Icon name="image" size={13} /> PNG
          </button>

          <button
            className="zz-toolbar-btn"
            onClick={handleExportPDF}
            title="Export as PDF"
            disabled={isExporting}
          >
            <Icon name="fileText" size={13} /> PDF
          </button>

          {timeline && (
            <button
              className="zz-toolbar-btn"
              onClick={() => setEmbedOpen(true)}
              title={isPublic ? 'Get embed code' : 'Make the timeline public to embed it'}
              disabled={!isPublic}
            >
              <Icon name="code" size={13} /> Embed
            </button>
          )}
        </div>
      )}

      {/* Centred vertical spine */}
      <div
        className="zz-spine"
        aria-hidden="true"
        style={{ background: `linear-gradient(${colors.primary}60, ${spineColor}30)` }}
      />

      <ol className="zz-list" aria-label="Timeline events">
        {displayItems.map(item => {
          if (item.type === 'divider') {
            return <ZigzagDivider key={item.id} label={item.label} colors={colors} />;
          }

          if (item.type === 'featured') {
            return (
              <ZigzagFeaturedCard
                key={item.id}
                ev={item.ev}
                colors={colors}
                onEventClick={onEventClick}
              />
            );
          }

          // Regular alternating event
          const { ev, isLeft, dotIndex } = item;
          return (
            <li key={item.id} className={`zz-item ${isLeft ? 'zz-left' : 'zz-right'}`}>
              {isLeft ? (
                <div className="zz-side zz-card-side">
                  <ZigzagCard ev={ev} isLeft colors={colors} onEventClick={onEventClick} photoFirst={photoFirst} cardIndex={dotIndex} />
                </div>
              ) : (
                <div className="zz-side zz-empty-side" aria-hidden="true" />
              )}

              <div className="zz-middle" aria-hidden="true">
                <div className="zz-connector" style={{ background: `${spineColor}60` }} />
                <div
                  className="zz-dot"
                  style={{
                    background: spineColor,
                    '--dot-index': dotIndex,
                  }}
                />
              </div>

              {isLeft ? (
                <div className="zz-side zz-empty-side" aria-hidden="true" />
              ) : (
                <div className="zz-side zz-card-side">
                  <ZigzagCard ev={ev} isLeft={false} colors={colors} onEventClick={onEventClick} photoFirst={photoFirst} cardIndex={dotIndex} />
                </div>
              )}
            </li>
          );
        })}
      </ol>

      {/* Embed modal */}
      {timeline && (
        <Modal
          open={embedOpen}
          onClose={() => { setEmbedOpen(false); setEmbedCopied(false); }}
          title="Embed Timeline"
          width={580}
        >
          <p style={{ fontSize: 14, color: 'var(--text2)', marginBottom: 16, lineHeight: 1.6 }}>
            Paste this snippet into any webpage to embed{' '}
            <strong style={{ color: 'var(--text)' }}>{timeline?.title}</strong> as an interactive iframe.
          </p>

          <div style={{
            background: 'var(--bg3)', border: '1px solid var(--border)',
            borderRadius: 'var(--radius)', overflow: 'hidden', marginBottom: 12,
          }}>
            <div style={{ padding: '6px 14px', borderBottom: '1px solid var(--border)' }}>
              <span style={{ fontSize: 11, color: 'var(--text3)', fontFamily: 'monospace' }}>HTML</span>
            </div>
            <pre style={{
              padding: '14px 16px', margin: 0, fontSize: 12, lineHeight: 1.75,
              color: 'var(--accent2)', fontFamily: 'monospace', overflowX: 'auto',
              whiteSpace: 'pre-wrap', wordBreak: 'break-all',
            }}>
              {embedCode}
            </pre>
          </div>

          <div className="ht-embed-controls">
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--text2)', cursor: 'pointer' }}>
              <input type="checkbox" checked={embedResp} onChange={e => setEmbedResp(e.target.checked)} style={{ width: 'auto' }} />
              Responsive (100% width)
            </label>
            {!embedResp && (
              <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                <div style={{ flex: 1 }}>
                  <label style={{ marginBottom: 4 }}>Width</label>
                  <input value={embedWidth} onChange={e => setEmbedWidth(e.target.value)} placeholder="e.g. 800 or 100%" style={{ fontSize: 13 }} />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ marginBottom: 4 }}>Height (px)</label>
                  <input value={embedHeight} onChange={e => setEmbedHeight(e.target.value)} placeholder="e.g. 700" style={{ fontSize: 13 }} />
                </div>
              </div>
            )}
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {[['Small', '600', '500'], ['Medium', '800', '700'], ['Large', '1000', '900']].map(([name, w, h]) => (
                <button
                  key={name}
                  className="zz-toolbar-btn"
                  onClick={() => { setEmbedWidth(w); setEmbedHeight(h); setEmbedResp(false); }}
                  style={{ fontSize: 12 }}
                >
                  {name} ({w}×{h})
                </button>
              ))}
            </div>
          </div>

          <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 20 }}>
            <button className="btn-ghost" onClick={() => { setEmbedOpen(false); setEmbedCopied(false); }}>
              Close
            </button>
            <button
              className="btn-primary"
              onClick={copyEmbed}
              style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 148 }}
            >
              <Icon name={embedCopied ? 'check' : 'copy'} size={14} />
              {embedCopied ? 'Copied!' : 'Copy Snippet'}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default ZigzagTimeline;
